import { AnimatePresence, motion } from 'framer-motion';
import {
    AlertCircle, CalendarDays, CreditCard,
    FileText, Hash, Landmark, PackagePlus, Plus,
    ReceiptText, Trash2, Wallet
} from 'lucide-react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import { BASE_URL_API } from '../../References/URLs';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AuthContext from '../../store/auth-context';
import styles from './AddNewExpense.module.css';

/* ─── helpers ─────────────────────────────────── */
let _idSeq = 1;
const newItemId = () => `item_${Date.now()}_${_idSeq++}`;

const EMPTY_ITEM = () => ({
    _id: newItemId(),
    expenseName: '',
    storeCategoryIdentifier: '',
    storeSubcategoryIdentifier: '',
    amount: '',
    gstRate: '',
    expenseNotes: '',
});

const PAYMENT_FIELDS = [
    { key: 'paidViaCash',    label: 'Cash',            icon: Wallet },
    { key: 'paidViaQr',      label: 'QR / UPI',        icon: Hash },
    { key: 'paidViaOnline',  label: 'Online Transfer', icon: Landmark },
    { key: 'paidViaCheque',  label: 'Cheque',          icon: FileText },
    { key: 'paidViaCard',    label: 'Card',            icon: CreditCard },
    { key: 'paidViaCredit',  label: 'Credit',          icon: ReceiptText },
];

const MAX_CURRENCY_VALUE = 99999999.99;
const MAX_GST_RATE = 100;
const CURRENCY_INPUT_MAX_LENGTH = 11;
const GST_INPUT_MAX_LENGTH = 6;

const CATEGORY_SELECT_WIDTH = 188;
const SUBCATEGORY_SELECT_WIDTH = 188;

const notifyExpenseMutation = () => {
    window.dispatchEvent(new CustomEvent('expenses-dashboard:refresh'));
};

const getSelectStyles = ({ width, isDisabled = false }) => ({
    container: (base) => ({
        ...base,
        width,
        minWidth: width,
        maxWidth: width,
    }),
    control: (base, state) => ({
        ...base,
        minHeight: 35,
        borderRadius: 8,
        borderColor: state.isFocused ? '#6d28d9' : '#e2e8f0',
        backgroundColor: isDisabled ? '#f8fafc' : '#ffffff',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(109, 40, 217, 0.09)' : 'none',
        cursor: isDisabled ? 'default' : 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': {
            borderColor: state.isFocused ? '#6d28d9' : '#cbd5e1',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 8px',
        fontSize: '0.8rem',
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        color: '#334155',
    }),
    singleValue: (base) => ({
        ...base,
        color: isDisabled ? '#94a3b8' : '#334155',
        fontSize: '0.8rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#94a3b8',
        fontSize: '0.8rem',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: 33,
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#94a3b8',
        padding: 6,
        '&:hover': { color: '#64748b' },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    menu: (base) => ({
        ...base,
        width,
        minWidth: width,
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        boxShadow: '0 12px 30px rgba(15, 23, 42, 0.12)',
        overflow: 'hidden',
    }),
    menuList: (base) => ({
        ...base,
        padding: 6,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '0.8rem',
        borderRadius: 8,
        padding: '8px 10px',
        backgroundColor: state.isSelected ? '#ede9fe' : state.isFocused ? '#f5f3ff' : '#ffffff',
        color: state.isSelected ? '#5b21b6' : '#334155',
        cursor: 'pointer',
    }),
    noOptionsMessage: (base) => ({
        ...base,
        fontSize: '0.78rem',
        color: '#94a3b8',
    }),
});

const fmt = (n) => {
    const num = parseFloat(n);
    return isNaN(num) ? '0.00' : num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sanitizeDecimalInput = (
    rawValue,
    { maxIntegerDigits, maxDecimalDigits = 2, maxValue }
) => {
    if (rawValue === '') return '';

    let nextValue = String(rawValue).replace(/[^\d.]/g, '');
    if (!nextValue) return '';

    const firstDotIndex = nextValue.indexOf('.');
    if (firstDotIndex !== -1) {
        nextValue = `${nextValue.slice(0, firstDotIndex + 1)}${nextValue.slice(firstDotIndex + 1).replace(/\./g, '')}`;
    }

    if (nextValue.startsWith('.')) {
        nextValue = `0${nextValue}`;
    }

    const hasDot = nextValue.includes('.');
    let [integerPart, decimalPart = ''] = nextValue.split('.');

    integerPart = integerPart.replace(/^0+(?=\d)/, '');
    if (integerPart === '') integerPart = '0';
    integerPart = integerPart.slice(0, maxIntegerDigits);
    decimalPart = decimalPart.slice(0, maxDecimalDigits);

    let sanitizedValue = hasDot ? `${integerPart}.${decimalPart}` : integerPart;
    if (hasDot && decimalPart === '') {
        sanitizedValue = `${integerPart}.`;
    }

    const numericValue = parseFloat(sanitizedValue);
    if (!isNaN(numericValue) && numericValue > maxValue) {
        sanitizedValue = maxValue.toFixed(maxDecimalDigits);
    }

    return sanitizedValue;
};

const todayStr = () => new Date().toISOString().split('T')[0];

/* ─── Component ───────────────────────────────── */
const AddNewExpense = () => {
    const authCtx = useContext(AuthContext);
    const asCtx   = useContext(ActiveStoreContext);
    const activeStore = (() => {
        try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== '') ? JSON.parse(asCtx.activeStore) : null; }
        catch { return null; }
    })();
    const storeIdentifier  = activeStore?.storeIdentifier ?? null;
    const vendorIdentifier = JSON.parse(authCtx?.loginData || '{}')?.partnersData?.vendorIdentifier;
    const authIdentifier   = authCtx?.authIdentifier ?? '';

    /* ── header fields ── */
    const [expenseDate,          setExpenseDate]          = useState(todayStr());
    const [vendorInvoiceNumber,  setVendorInvoiceNumber]  = useState('');
    const [headerNotes,          setHeaderNotes]          = useState('');

    /* ── line items ── */
    const [items, setItems] = useState([EMPTY_ITEM()]);
    /* per-item, last-typed text in the category select (not state — read only on selection) */
    const categoryInputTextRef = useRef({});

    /* ── payment breakdown ── */
    const [payment, setPayment] = useState({
        paidViaCash: '', paidViaQr: '', paidViaOnline: '',
        paidViaCheque: '', paidViaCard: '', paidViaCredit: '',
    });

    /* ── store expense categories ── */
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [catLoading,        setCatLoading]        = useState(false);
    const [isMobileViewport,  setIsMobileViewport]  = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.innerWidth <= 640;
    });

    /* ── submit / result ── */
    const [isSubmitting,  setIsSubmitting]  = useState(false);
    const [successResult, setSuccessResult] = useState(null);

    useEffect(() => {
        const handleViewportResize = () => {
            setIsMobileViewport(window.innerWidth <= 640);
        };

        window.addEventListener('resize', handleViewportResize);
        return () => {
            window.removeEventListener('resize', handleViewportResize);
        };
    }, []);

    const categoryOptions = useMemo(() => (
        expenseCategories
            .filter(c => c.isActive === 1 || c.isActive === '1')
            .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
            .map(c => ({
                value: c.storeCategoryIdentifier,
                label: c.categoryLabel,
            }))
    ), [expenseCategories]);

            const categorySelectWidth = isMobileViewport ? '100%' : CATEGORY_SELECT_WIDTH;
            const subcategorySelectWidth = isMobileViewport ? '100%' : SUBCATEGORY_SELECT_WIDTH;

    /* ─── fetch store expense categories ─── */
    const fetchCategories = useCallback(async () => {
        if (!storeIdentifier) return;
        setCatLoading(true);
        try {
            const res  = await fetch(BASE_URL_API + 'fetchStoreExpensesCategoryData', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier, includeInactive: false }),
            });
            const data = await res.json();
            if (data.status === 'success') {
                setExpenseCategories(Array.isArray(data.response?.categories) ? data.response.categories : []);
            } else if (data.message === 'tokenExpired') {
                authCtx.logout();
            }
        } catch (e) {
            console.error('fetchStoreExpensesCategoryData', e);
        }
        setCatLoading(false);
    }, [storeIdentifier, authCtx]);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    /* ─── item helpers ─── */
    const setItemField = (id, field, value) => {
        setItems(prev => prev.map(it =>
            it._id === id
                ? { ...it, [field]: value, ...(field === 'storeCategoryIdentifier' ? { storeSubcategoryIdentifier: '' } : {}) }
                : it
        ));
    };

    const setItemCurrencyField = (id, value) => {
        setItemField(id, 'amount', sanitizeDecimalInput(value, {
            maxIntegerDigits: 8,
            maxDecimalDigits: 2,
            maxValue: MAX_CURRENCY_VALUE,
        }));
    };

    const setItemGstField = (id, value) => {
        setItemField(id, 'gstRate', sanitizeDecimalInput(value, {
            maxIntegerDigits: 3,
            maxDecimalDigits: 2,
            maxValue: MAX_GST_RATE,
        }));
    };

    const setPaymentField = (field, value) => {
        setPayment(prev => ({
            ...prev,
            [field]: sanitizeDecimalInput(value, {
                maxIntegerDigits: 8,
                maxDecimalDigits: 2,
                maxValue: MAX_CURRENCY_VALUE,
            }),
        }));
    };

    const addItem    = () => setItems(prev => [...prev, EMPTY_ITEM()]);
    const removeItem = (id) => setItems(prev => prev.length > 1 ? prev.filter(it => it._id !== id) : prev);

    /* ─── derived totals ─── */
    const subTotal = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
    const totalGst = items.reduce((s, it) => {
        const amt  = parseFloat(it.amount) || 0;
        const rate = parseFloat(it.gstRate) || 0;
        return s + (amt * rate / 100);
    }, 0);
    const totalExpense = subTotal + totalGst;
    const totalPaid    = PAYMENT_FIELDS.reduce((s, f) => s + (parseFloat(payment[f.key]) || 0), 0);
    const balanceDue   = totalExpense - totalPaid;
    const isZeroState = totalExpense <= 0.005 && totalPaid <= 0.005;
    const isSettled = !isZeroState && totalExpense > 0.005 && Math.abs(balanceDue) <= 0.005;
    const isBalanceDue = !isZeroState && balanceDue > 0.005;
    const isAdvancePaid = !isZeroState && balanceDue < -0.005;

    /* ─── validation ─── */
    const validate = () => {
        const t = { position: 'top-center', hideProgressBar: true, autoClose: 3200 };
        if (!storeIdentifier) { toast.error('No active store selected.', t); return false; }
        if (!expenseDate)     { toast.error('Expense date is required.', t); return false; }
        for (let i = 0; i < items.length; i++) {
            const it = items[i];
            if (!it.expenseName.trim())            { toast.error(`Item ${i + 1}: expense name is required.`,   t); return false; }
            if (!it.storeCategoryIdentifier)       { toast.error(`Item ${i + 1}: select a category.`,          t); return false; }
            if (!it.storeSubcategoryIdentifier)    { toast.error(`Item ${i + 1}: select a subcategory.`,       t); return false; }
            if (!it.amount || parseFloat(it.amount) <= 0)
                                                   { toast.error(`Item ${i + 1}: enter a valid amount.`,       t); return false; }
            if ((parseFloat(it.amount) || 0) > MAX_CURRENCY_VALUE)
                                                   { toast.error(`Item ${i + 1}: amount cannot exceed ₹${fmt(MAX_CURRENCY_VALUE)}.`, t); return false; }
            if (it.gstRate !== '' && ((parseFloat(it.gstRate) || 0) > MAX_GST_RATE))
                                                   { toast.error(`Item ${i + 1}: GST rate cannot exceed ${MAX_GST_RATE}%.`, t); return false; }
        }
        for (const paymentField of PAYMENT_FIELDS) {
            if ((parseFloat(payment[paymentField.key]) || 0) > MAX_CURRENCY_VALUE) {
                toast.error(`${paymentField.label}: value cannot exceed ₹${fmt(MAX_CURRENCY_VALUE)}.`, t);
                return false;
            }
        }
        return true;
    };

    /* ─── submit ─── */
    const handleSubmit = async () => {
        if (!validate()) return;
        setIsSubmitting(true);
        try {
            const payload = {
                token: authCtx.token,
                storeIdentifier,
                authIdentifier,
                vendorIdentifier,
                expenseDate,
                vendorInvoiceNumber: vendorInvoiceNumber.trim() || undefined,
                expenseNotes:        headerNotes.trim()         || undefined,
                paymentMode: {
                    paidViaCash:   parseFloat(payment.paidViaCash)   || 0,
                    paidViaQr:     parseFloat(payment.paidViaQr)     || 0,
                    paidViaOnline: parseFloat(payment.paidViaOnline) || 0,
                    paidViaCheque: parseFloat(payment.paidViaCheque) || 0,
                    paidViaCard:   parseFloat(payment.paidViaCard)   || 0,
                    paidViaCredit: parseFloat(payment.paidViaCredit) || 0,
                },
                items: items.map(it => ({
                    expenseName:               it.expenseName.trim(),
                    storeCategoryIdentifier:   it.storeCategoryIdentifier,
                    storeSubcategoryIdentifier: it.storeSubcategoryIdentifier,
                    amount:                    parseFloat(it.amount),
                    gstRate:                   it.gstRate !== '' ? parseFloat(it.gstRate) : undefined,
                    expenseNotes:              it.expenseNotes.trim() || undefined,
                })),
            };

            const res  = await fetch(BASE_URL_API + 'addExpenses', { method: 'POST', body: JSON.stringify(payload) });
            const data = await res.json();

            if (data.status === 'success') {
                notifyExpenseMutation();
                setSuccessResult(data.response);
            } else if (data.message === 'tokenExpired') {
                authCtx.logout();
            } else {
                toast.error(data.message || 'Failed to add expense.', { position: 'top-center', hideProgressBar: true, autoClose: 4000 });
            }
        } catch {
            toast.error('Server error. Please try again.', { position: 'top-center', hideProgressBar: true, autoClose: 3200 });
        }
        setIsSubmitting(false);
    };

    const handleSubmitShortcut = useCallback((event) => {
        if (!(event.metaKey || event.ctrlKey) || event.key !== 'Enter') return;
        if (event.isComposing || isSubmitting) return;

        const targetElement = event.target;
        if (!(targetElement instanceof Element)) return;

        const activeFormElement = targetElement.closest('input, textarea, [role="combobox"], [contenteditable="true"]');
        if (!activeFormElement) return;

        event.preventDefault();
        handleSubmit();
    }, [handleSubmit, isSubmitting]);

    /* ─── reset ─── */
    const resetForm = () => {
        setExpenseDate(todayStr());
        setVendorInvoiceNumber('');
        setHeaderNotes('');
        setItems([EMPTY_ITEM()]);
        setPayment({ paidViaCash: '', paidViaQr: '', paidViaOnline: '', paidViaCheque: '', paidViaCard: '', paidViaCredit: '' });
        setSuccessResult(null);
    };

    /* ─── sub-categories helper ─── */
    const getSubcats = (catId) => {
        const cat = expenseCategories.find(c => c.storeCategoryIdentifier === catId);
        return Array.isArray(cat?.subcategories)
            ? cat.subcategories.filter(s => s.isActive === 1 || s.isActive === '1')
            : [];
    };
    const getSubcategoryOptions = (catId) => (
        getSubcats(catId)
            .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
            .map((subcategory) => ({
                value: subcategory.storeSubcategoryIdentifier,
                label: subcategory.subcategoryLabel,
            }))
    );

    /* ─── category select: search categories, but also match on subcategory names ─── */
    const findSubcategoryMatch = (catId, searchText) => {
        if (!searchText) return null;
        return getSubcats(catId).find(s => s.subcategoryLabel.toLowerCase().includes(searchText)) || null;
    };

    const categoryFilterOption = (option, inputValue) => {
        const search = inputValue.trim().toLowerCase();
        if (!search) return true;
        if (option.label.toLowerCase().includes(search)) return true;
        return Boolean(findSubcategoryMatch(option.value, search));
    };

    const formatCategoryOptionLabel = (option, { context, inputValue }) => {
        const search = inputValue.trim().toLowerCase();
        if (context !== 'menu' || !search || option.label.toLowerCase().includes(search)) {
            return option.label;
        }
        const matchedSubcategory = findSubcategoryMatch(option.value, search);
        if (!matchedSubcategory) return option.label;
        return (
            <span>
                {option.label}
                <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 6, fontSize: '0.72em' }}>
                    via "{matchedSubcategory.subcategoryLabel}"
                </span>
            </span>
        );
    };

    const handleCategoryChange = (itemId, option) => {
        const typedText = (categoryInputTextRef.current[itemId] || '').trim().toLowerCase();
        let matchedSubcategoryId = '';

        if (option && typedText && !option.label.toLowerCase().includes(typedText)) {
            const matchedSubcategory = findSubcategoryMatch(option.value, typedText);
            if (matchedSubcategory) matchedSubcategoryId = matchedSubcategory.storeSubcategoryIdentifier;
        }

        setItems(prev => prev.map(it => it._id === itemId
            ? { ...it, storeCategoryIdentifier: option?.value || '', storeSubcategoryIdentifier: matchedSubcategoryId }
            : it));
    };

    /* ─── No store guard ─── */
    if (!storeIdentifier) {
        return (
            <div className={styles.noStoreWrapper}>
                <div className={styles.noStoreCard}>
                    <Wallet size={36} className={styles.noStoreIcon} />
                    <h3>No Store Selected</h3>
                    <p>Please select a store from the top bar before recording an expense.</p>
                    <Link to="/expenses" className={styles.noStoreBack}>← Back to Expenses</Link>
                </div>
            </div>
        );
    }

    /* ─── Success screen ─── */
    if (successResult) {
        const r = successResult;
        const statusColors = {
            paid:    { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
            partial: { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
            unpaid:  { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' },
        };
        const sc = statusColors[r.paymentStatus] ?? statusColors.partial;
        return (
            <div className={styles.mainWrapper}>
                <motion.div
                    className={styles.successCard}
                    initial={{ opacity: 0, scale: 0.94, y: 24 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                >
                    <div className={styles.successIcon}>✓</div>
                    <h2 className={styles.successTitle}>Expense Recorded</h2>
                    <p className={styles.successRef}>{r.expenseIdentifier}</p>

                    <div className={styles.successGrid}>
                        <div className={styles.successGridItem}><span>Date</span><strong>{r.expenseDate}</strong></div>
                        <div className={styles.successGridItem}><span>Period</span><strong>{r.fiscalPeriod}</strong></div>
                        <div className={styles.successGridItem}><span>Items</span><strong>{r.totalItems}</strong></div>
                        <div className={styles.successGridItem}><span>Sub-total</span><strong>₹{fmt(r.subTotal)}</strong></div>
                        <div className={styles.successGridItem}><span>GST</span><strong>₹{fmt(r.totalGst)}</strong></div>
                        <div className={styles.successGridItem}><span>Total</span><strong>₹{fmt(r.totalExpense)}</strong></div>
                        <div className={styles.successGridItem}><span>Paid</span><strong>₹{fmt(r.totalPaid)}</strong></div>
                        <div className={styles.successGridItem}><span>Balance</span><strong>₹{fmt(r.balanceDue)}</strong></div>
                        <div className={`${styles.successGridItem} ${styles.successGridFull}`}>
                            <span>Status</span>
                            <strong style={{ color: sc.text, background: sc.bg, border: `1px solid ${sc.border}`, borderRadius: '6px', padding: '2px 10px', fontSize: '0.75rem', textTransform: 'capitalize' }}>
                                {r.paymentStatus}
                            </strong>
                        </div>
                    </div>

                    <div className={styles.successActions}>
                        <button className={styles.successAddBtn} onClick={resetForm}>
                            <Plus size={15} /> Add Another Expense
                        </button>
                        <Link to="/expenses" className={styles.successBackBtn}>← Back to Expenses</Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    /* ─── Main form ─── */
    return (
        <div className={styles.mainWrapper} onKeyDownCapture={handleSubmitShortcut}>

            {/* ── Page Header ── */}
            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderLeft}>
                    <div className={styles.pageHeaderMeta}>
                        <PackagePlus size={14} className={styles.pageHeaderMetaIcon} />
                        <span>Expenses</span>
                        <span className={styles.breadcrumbSep}>›</span>
                        <span>New Entry</span>
                    </div>
                    <h1 className={styles.pageTitle}>Add New Expense</h1>
                    <p className={styles.pageSubtitle}>Record a multi-item purchase with payment breakdown and GST</p>
                </div>
                <div className={styles.pageHeaderRight}>
                    <Link to="/expenses" className={styles.secondaryBtn}>← Back</Link>
                </div>
            </div>

            {/* ── Body grid ── */}
            <div className={styles.bodyGrid}>

                {/* ── LEFT col: details + items ── */}
                <div className={styles.leftCol}>

                    {/* Header details card */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <CalendarDays size={14} className={styles.cardHeaderIcon} />
                            <span className={styles.cardHeaderTitle}>Expense Details</span>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.detailsGrid}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        Date <span className={styles.req}>*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={expenseDate}
                                        onChange={e => setExpenseDate(e.target.value)}
                                        className={styles.fieldInput}
                                        max={todayStr()}
                                    />
                                </div>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Invoice / Bill No.</label>
                                    <input
                                        type="text"
                                        value={vendorInvoiceNumber}
                                        onChange={e => setVendorInvoiceNumber(e.target.value)}
                                        placeholder="INV-2025-441"
                                        className={styles.fieldInput}
                                        maxLength={80}
                                    />
                                </div>
                            </div>
                            <div className={styles.fieldGroup} style={{ marginTop: '0.75rem' }}>
                                <label className={styles.fieldLabel}>
                                    Notes <span className={styles.fieldOptional}>(optional)</span>
                                </label>
                                <textarea
                                    value={headerNotes}
                                    onChange={e => setHeaderNotes(e.target.value)}
                                    placeholder="e.g. Monthly stock purchase from Wella distributor"
                                    className={styles.fieldTextarea}
                                    rows={2}
                                    maxLength={400}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Items card */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <ReceiptText size={14} className={styles.cardHeaderIcon} />
                            <span className={styles.cardHeaderTitle}>Expense Line Items</span>
                            <span className={styles.cardHeaderCount}>
                                {items.length} item{items.length !== 1 ? 's' : ''}
                            </span>
                        </div>

                        {/* Column labels */}
                        <div className={styles.itemColHeader}>
                            <div className={styles.colIdx}>#</div>
                            <div className={styles.colMain}>Item description &amp; category</div>
                            <div className={styles.colAmt}>Amount</div>
                            <div className={styles.colGst}>GST %</div>
                            <div className={styles.colDel} />
                        </div>

                        <div className={styles.itemList}>
                            <AnimatePresence initial={false}>
                                {items.map((item, idx) => {
                                    const subcats   = getSubcats(item.storeCategoryIdentifier);
                                    const itemTotal = (parseFloat(item.amount) || 0) * (1 + (parseFloat(item.gstRate) || 0) / 100);
                                    return (
                                        <motion.div
                                            key={item._id}
                                            className={styles.itemRow}
                                            initial={{ opacity: 0, y: -8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, height: 0, marginBottom: 0, overflow: 'hidden' }}
                                            transition={{ duration: 0.18 }}
                                        >
                                            <div className={styles.itemRowHead}>
                                                <div className={styles.itemIndex}>Item {idx + 1}</div>
                                                <button
                                                    className={styles.removeItemBtn}
                                                    onClick={() => removeItem(item._id)}
                                                    disabled={items.length === 1}
                                                    title="Remove this item"
                                                >
                                                    <Trash2 size={13} strokeWidth={2.2} />
                                                </button>
                                            </div>

                                            <div className={styles.itemFields}>
                                                {/* Row 1 — name */}
                                                <input
                                                    type="text"
                                                    value={item.expenseName}
                                                    onChange={e => setItemField(item._id, 'expenseName', e.target.value)}
                                                    placeholder="Expense name e.g. Wella Color Charm — 6 boxes"
                                                    className={styles.itemNameInput}
                                                    maxLength={200}
                                                />
                                                {/* Row 2 — selects + amount + gst */}
                                                <div className={styles.itemRow2}>
                                                    <div className={styles.selectWrapFixed}>
                                                        {catLoading ? (
                                                            <div className={styles.selectSkeleton} />
                                                        ) : (
                                                            <Select
                                                                classNamePrefix="expenseSelect"
                                                                options={categoryOptions}
                                                                value={categoryOptions.find(option => option.value === item.storeCategoryIdentifier) || null}
                                                                onChange={(option) => handleCategoryChange(item._id, option)}
                                                                onInputChange={(value, meta) => {
                                                                    if (meta.action === 'input-change') {
                                                                        categoryInputTextRef.current[item._id] = value;
                                                                    }
                                                                    return value;
                                                                }}
                                                                filterOption={categoryFilterOption}
                                                                formatOptionLabel={formatCategoryOptionLabel}
                                                                placeholder="Category"
                                                                isSearchable
                                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                                menuPosition="fixed"
                                                                styles={getSelectStyles({ width: categorySelectWidth })}
                                                            />
                                                        )}
                                                    </div>

                                                    <div className={styles.selectWrapFixed}>
                                                        <Select
                                                            classNamePrefix="expenseSelect"
                                                            options={getSubcategoryOptions(item.storeCategoryIdentifier)}
                                                            value={getSubcategoryOptions(item.storeCategoryIdentifier).find(option => option.value === item.storeSubcategoryIdentifier) || null}
                                                            onChange={(option) => setItemField(item._id, 'storeSubcategoryIdentifier', option?.value || '')}
                                                            placeholder="Subcategory"
                                                            isSearchable
                                                            isDisabled={!item.storeCategoryIdentifier || subcats.length === 0}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            menuPosition="fixed"
                                                            styles={getSelectStyles({ width: subcategorySelectWidth, isDisabled: !item.storeCategoryIdentifier || subcats.length === 0 })}
                                                            noOptionsMessage={() => item.storeCategoryIdentifier ? 'No subcategories' : 'Choose a category first'}
                                                        />
                                                    </div>

                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={item.amount}
                                                        onChange={e => setItemCurrencyField(item._id, e.target.value)}
                                                        placeholder="₹ Amount"
                                                        className={`${styles.itemInput} ${styles.itemAmtInput}`}
                                                        maxLength={CURRENCY_INPUT_MAX_LENGTH}
                                                    />

                                                    <div className={styles.gstInputWrap}>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={item.gstRate}
                                                            onChange={e => setItemGstField(item._id, e.target.value)}
                                                            placeholder="GST %"
                                                            className={`${styles.itemInput} ${styles.itemGstInput}`}
                                                            maxLength={GST_INPUT_MAX_LENGTH}
                                                        />
                                                        <span className={styles.gstPct}>%</span>
                                                    </div>
                                                </div>

                                                {/* Row 3 — notes + running total */}
                                                <div className={styles.itemRow3}>
                                                    <input
                                                        type="text"
                                                        value={item.expenseNotes}
                                                        onChange={e => setItemField(item._id, 'expenseNotes', e.target.value)}
                                                        placeholder="Item notes (optional)"
                                                        className={styles.itemNotesInput}
                                                        maxLength={300}
                                                    />
                                                    {parseFloat(item.amount) > 0 && (
                                                        <span className={styles.itemTotal}>₹{fmt(itemTotal)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </div>

                        <button className={styles.addItemBtn} onClick={addItem}>
                            <Plus size={14} /> Add Line Item
                        </button>
                    </div>

                </div>

                {/* ── RIGHT col: payment + summary ── */}
                <div className={styles.rightCol}>

                    {/* Payment card */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <Wallet size={14} className={styles.cardHeaderIcon} />
                            <span className={styles.cardHeaderTitle}>Payment Breakdown</span>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.paymentGrid}>
                                {PAYMENT_FIELDS.map(({ key, label, icon: Icon }) => (
                                    <div key={key} className={styles.paymentField}>
                                        <label className={styles.paymentLabel}>
                                            <Icon size={12} className={styles.paymentLabelIcon} />
                                            {label}
                                        </label>
                                        <div className={styles.paymentInputWrap}>
                                            <span className={styles.paymentCurrency}>₹</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={payment[key]}
                                                onChange={e => setPaymentField(key, e.target.value)}
                                                placeholder="0.00"
                                                className={styles.paymentInput}
                                                maxLength={CURRENCY_INPUT_MAX_LENGTH}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Summary + submit */}
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryRow}>
                            <span>Sub-total</span><span>₹{fmt(subTotal)}</span>
                        </div>
                        <div className={styles.summaryRow}>
                            <span>GST</span><span>₹{fmt(totalGst)}</span>
                        </div>
                        <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                            <span>Total</span><span>₹{fmt(totalExpense)}</span>
                        </div>
                        <div className={styles.summaryDivider} />
                        <div className={styles.summaryRow}>
                            <span>Total Paid</span><span>₹{fmt(totalPaid)}</span>
                        </div>
                        <div
                            className={`${styles.summaryRow} ${
                                isZeroState
                                    ? styles.summaryNeutral
                                    : isBalanceDue
                                        ? styles.summaryDue
                                        : isAdvancePaid
                                            ? styles.summaryAdvance
                                            : styles.summarySettled
                            }`}
                        >
                            <span>
                                {isZeroState
                                    ? 'Awaiting items'
                                    : isBalanceDue
                                        ? 'Balance Due'
                                        : isAdvancePaid
                                            ? 'Advance Paid'
                                            : 'Settled'}
                            </span>
                            <span>
                                {isZeroState
                                    ? 'Add line items to calculate'
                                    : isBalanceDue
                                        ? `₹${fmt(balanceDue)}`
                                        : isAdvancePaid
                                            ? `₹${fmt(Math.abs(balanceDue))}`
                                            : '✓ Paid in full'}
                            </span>
                        </div>

                        <button
                            className={styles.submitBtn}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? <><span className={styles.spinner} /> Saving…</>
                                : <><PackagePlus size={14} /> Save Expense</>
                            }
                        </button>

                        <div className={styles.submitHint}>Tip: press ⌘/Ctrl + Enter to submit</div>

                        <Link to="/expenses" className={styles.cancelLink}>Cancel — discard changes</Link>
                    </div>

                    {/* Warning if no categories */}
                    {!catLoading && expenseCategories.length === 0 && (
                        <div className={styles.warnCard}>
                            <AlertCircle size={14} className={styles.warnIcon} />
                            <span>
                                No active categories for this store.{' '}
                                <Link to="/settings/expenses" className={styles.warnLink}>
                                    Set them up in Settings →
                                </Link>
                            </span>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default AddNewExpense;
